/**
 * Archivo privado de historias (oct-2026, founder) — mis historias caducadas (y las activas) para agregarlas a
 * una destacada. Las caducadas se guardan 90 días; las que ya están en una destacada, para siempre.
 * Props: open, onOpenChange, excludeIds (ya en la destacada), onConfirm(storyIds) → cierra el padre.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Play, X, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { highlightsApi } from '@/lib/highlightsApi';
import { friendlyError } from '@/lib/apiError';
import { toast } from 'sonner';
import { tr, localeTag } from '@/contexts/LanguageContext';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(localeTag(), { day: 'numeric', month: 'short' }) : '');

export const StoryThumb = ({ story, selected, badge, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`relative aspect-[9/14] rounded-xl overflow-hidden border-2 bg-muted text-left ${selected ? 'border-primary' : 'border-transparent'}`}>
    {story.kind === 'image' || story.thumbnail_url ? (
      <img src={story.kind === 'image' ? story.url : story.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
    ) : (
      <span className="w-full h-full flex items-center justify-center bg-black/60"><Play className="w-6 h-6 text-white/80" /></span>
    )}
    {story.kind === 'video' && <Play className="absolute top-1.5 left-1.5 w-3.5 h-3.5 text-white drop-shadow" />}
    <span className="absolute bottom-1 left-1.5 text-[9px] text-white/90 drop-shadow tabular-nums">{fmt(story.created_at)}</span>
    {badge}
    {selected && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center"><Check className="w-3 h-3" /></span>}
  </button>
);

export const StoryArchiveSheet = ({ open, onOpenChange, excludeIds = [], onConfirm, confirmLabel, title }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel([]); setData(null);
    highlightsApi.myStories('all').then(setData).catch((e) => { toast.error(friendlyError(e, tr("No pudimos cargar tu archivo"))); setData({ items: [] }); });
  }, [open]);

  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);
  const items = (data?.items || []).filter((s) => !exclude.has(s.id));
  const active = items.filter((s) => !s.expired);
  const archived = items.filter((s) => s.expired);
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const confirm = async () => {
    if (!sel.length) return;
    setBusy(true);
    try { await onConfirm?.(sel); onOpenChange?.(false); } catch (e) { toast.error(friendlyError(e, tr("No pudimos agregar las historias"))); } finally { setBusy(false); }
  };

  if (!open) return null;
  const Grid = ({ list, prefix }) => (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {list.map((s) => (
        <StoryThumb key={s.id} story={s} selected={sel.includes(s.id)} onClick={() => toggle(s.id)} testid={`${prefix}-${s.id}`}
          badge={s.highlight_ids?.length ? <span className="absolute top-1 left-1/2 -translate-x-1/2 rounded-full bg-black/60 text-white text-[8px] px-1.5 py-px" data-testid={`${prefix}-${s.id}-in`}>{tr("Destacada")}</span> : null} />
      ))}
    </div>
  );
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => !busy && onOpenChange?.(false)} data-testid="story-archive-sheet">
      <div className="w-full sm:max-w-lg max-h-[92vh] rounded-t-3xl sm:rounded-3xl border border-border bg-card flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="font-display text-base font-bold flex items-center gap-2"><Archive className="w-4 h-4 text-primary" />{title || tr("Mi archivo de historias")}</h3>
          <button type="button" onClick={() => onOpenChange?.(false)} className="w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center" aria-label={tr("Cerrar")} data-testid="story-archive-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="px-4 text-[11px] text-muted-foreground">{tr("Solo tú ves este archivo. Las historias caducadas se guardan 90 días; las que estén en una destacada se conservan para siempre.")}</p>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {data === null && <p className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />{tr("Cargando…")}</p>}
          {data && !items.length && <p className="text-sm text-muted-foreground text-center py-8" data-testid="story-archive-empty">{tr("Aún no tienes historias para agregar. Sube una historia y aparecerá aquí.")}</p>}
          {active.length > 0 && (<section><p className="text-[10px] uppercase tracking-widest text-primary font-bold mb-1.5">{tr("Activas (24 h)")}</p><Grid list={active} prefix="archive-active" /></section>)}
          {archived.length > 0 && (<section><p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5">{tr("Archivo · caducadas")}</p><Grid list={archived} prefix="archive-expired" /></section>)}
        </div>
        <div className="p-4 border-t border-border flex items-center gap-3">
          <p className="text-xs text-muted-foreground flex-1" data-testid="story-archive-count">{tr("{0} seleccionada(s)", sel.length)}</p>
          <Button className="rounded-full h-10 px-5" disabled={!sel.length || busy} onClick={confirm} data-testid="story-archive-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (confirmLabel || tr("Agregar a destacada"))}
          </Button>
        </div>
      </div>
    </div>, document.body);
};

export default StoryArchiveSheet;
